import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Grid, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { closeWorkAssignments } from 'wisestep-api';
import { EmptyTeamsAndUsersIcon, WsColors, WsDataGrid, WsListingLoader } from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import SearchBar from '../../../features/teams/components/SearchBarWrapper';
import WsAvatar from '../../../features/teams/components/WsAvatar';
import WsLink from '../../../features/teams/components/WsLink';
import TeamAvatar from '../../../images/Avatar/Group.svg';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { createAssignmentsObject } from '../components/helper/JobsCrmHelper';
import { useJobCRMContext } from '../context';
import useGetWorkAssignmentInfo from '../hooks/useGetWorkAssignmentInfo';

const TeamAllocation = () => {
  const [loading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const { job } = useJobCRMContext();
  const methods = useForm();
  const handlePage = (newPage: number) => setPage(newPage);
  const { workAssignmentInfo, loadWorkAssignmentInfo } = useGetWorkAssignmentInfo(job?.id ?? '');
  const setNotification = useNotificationStore((state) => state.setNotification);

  const handlePageSize = (newPageSize: number) => {
    handlePage(0);
    setPageSize(newPageSize);
  };

  const unAssignTeam = async (teamId) => {
    const assignments = createAssignmentsObject();
    try {
      if (job?.id) {
        assignments.jobDTOList.push(job?.id);
        assignments.teamDTOList.push(teamId);
        await closeWorkAssignments(assignments);
        loadWorkAssignmentInfo();
        setNotification(success('Unassigned to the team'));
      }
    } catch (e) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  const data = useMemo(
    () =>
      (workAssignmentInfo?.teams ?? [])?.map((team) => ({
        id: team?.id ?? '',
        teamName: team?.name ?? '',
      })),
    [workAssignmentInfo?.teams]
  );
  const columns: GridColDef[] = [
    {
      field: 'teamName',
      headerName: 'Team',
      sortable: false,
      flex: 2.5,
      renderCell: (params) => {
        return (
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <WsAvatar src={TeamAvatar} size="small" variant="square" />
            </Grid>
            <Grid item>
              <WsLink
                to={`/team/team-members-view?teamId=${params.row.id}&teamName=${params.value}`}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }}
              >
                {params.value}
              </WsLink>
            </Grid>
          </Grid>
        );
      },
    },
    {
      field: 'id',
      headerName: 'Un Assign',
      sortable: false,
      flex: 0.3,
      renderCell: (params) => (
        <IconButton onClick={() => unAssignTeam(params.value)}>
          <RemoveCircleIcon sx={{ fontSize: 20, color: WsColors.DarkRed }} />
        </IconButton>
      ),
    },
  ];
  return (
    <Grid container rowGap={1}>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <SearchBar name="query" />
        </FormProvider>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <WsListingLoader minHeight={400} />
        ) : (
          <>
            {data?.length ? (
              <WsDataGrid
                autoHeight
                rows={data}
                columns={columns}
                paginationMode="client"
                page={page ?? 0}
                onPageChange={handlePage}
                pageSize={pageSize ?? 20}
                onPageSizeChange={handlePageSize}
              />
            ) : (
              <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default TeamAllocation;
